import {
  evaluateSegmentDefinition,
  filterOptionsEndpointQuery,
  filterSchemaQuery,
  isGroupFilterNode,
  segmentQuery,
  useCreateSegmentMutation,
  useUpdateSegmentMutation,
  type BoolFieldSchema,
  type FilterFieldSchema,
  type FilterMatchInfo,
  type FilterNode,
  type FilterTypeSchema,
  type GroupFilterNode,
  type MultiSelectFieldSchema,
  type NumberFieldSchema,
  type ProseFieldSchema,
  type SegmentEvaluationResult,
  type SelectFieldSchema,
  type TextAreaFieldSchema,
  type TextFieldSchema,
} from "@/api/campaigns";
import { StyledLink } from "@/components/link/StyledLink";
import { Button } from "@/components/ui/Button";
import { PageWrapper } from "@/components/ui/PageWrapper";
import { seo } from "@/utils/seo";
import { Box, ErrorMessage, Field, Flex, Label, Text, toast } from "@givenwell/components";
import { Input } from "@/components/ui/Input";
import { colors } from "@givenwell/theme";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate, useParams } from "@tanstack/react-router";
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";

export const Route = createFileRoute("/campaigns/segments/$segmentId")({
  component: SegmentEditor,
  head: () => ({ meta: seo({ title: "Edit Segment" }) }),
});

// -- Dark theme style constants --

const darkSelect: React.CSSProperties = {
  background: "#444",
  border: "1px solid rgba(255,255,255,0.15)",
  borderRadius: 4,
  color: "#e0e0e0",
  padding: "4px 8px",
  fontSize: 13,
  cursor: "pointer",
  outline: "none",
};

const darkInput: React.CSSProperties = {
  background: "#444",
  border: "1px solid rgba(255,255,255,0.15)",
  borderRadius: 4,
  color: "#e0e0e0",
  padding: "4px 8px",
  fontSize: 13,
  outline: "none",
  flex: 1,
  minWidth: 80,
};

const combinatorSelect: React.CSSProperties = {
  ...darkSelect,
  background: "#555",
  fontWeight: 600,
  fontSize: 12,
  textAlign: "center",
  padding: "4px 12px",
};

const removeButton: React.CSSProperties = {
  background: "rgba(255,255,255,0.1)",
  border: "none",
  borderRadius: 4,
  color: "rgba(255,255,255,0.5)",
  cursor: "pointer",
  padding: "2px 6px",
  fontSize: 14,
  lineHeight: 1,
  flexShrink: 0,
};

const addButtonStyle: React.CSSProperties = {
  background: "none",
  border: "1px dashed rgba(255,255,255,0.15)",
  borderRadius: 4,
  color: "rgba(255,255,255,0.4)",
  cursor: "pointer",
  padding: "6px 12px",
  fontSize: 13,
};

const matchBadgeStyle: React.CSSProperties = {
  background: "rgba(255,255,255,0.08)",
  borderRadius: 10,
  color: "rgba(255,255,255,0.5)",
  fontSize: 11,
  fontWeight: 500,
  padding: "1px 8px",
  whiteSpace: "nowrap",
  flexShrink: 0,
};

function MatchBadge({ info }: { info?: FilterMatchInfo }) {
  if (!info) return null;
  return <span style={matchBadgeStyle}>{info.matched_count}</span>;
}

// -- Helpers --

function createFilterFromSchema(filterType: FilterTypeSchema): FilterNode {
  const node: Record<string, unknown> = { $type: filterType.type };
  for (const field of filterType.fields) {
    // Layout-only fields (prose) have no key and no value on the node.
    if (field.$type === "prose" || !field.key) continue;
    if (field.$type === "multi_select" || field.$type === "textarea") {
      node[field.key] = field.default_value ?? [];
    } else if (field.$type === "bool") {
      node[field.key] = field.default_value ?? false;
    } else if (field.$type === "number") {
      node[field.key] = field.default_value ?? 0;
    } else if (field.default_value != null) {
      node[field.key] = field.default_value;
    } else {
      node[field.key] = "";
    }
  }
  return node as FilterNode;
}

function createEmptyGroup(): GroupFilterNode {
  return {
    $type: "group",
    combinator: "and",
    filters: [],
  };
}

const addSelectStyle: React.CSSProperties = {
  ...addButtonStyle,
  appearance: "none",
  WebkitAppearance: "none",
};

// -- Main component --

function SegmentEditor() {
  const params = useParams({ strict: false });
  const isNew = !params.segmentId || params.segmentId === "new";
  const navigate = useNavigate();

  const segmentId = isNew ? "" : params.segmentId!;
  const query = useQuery({ ...segmentQuery(segmentId), enabled: !isNew });
  const schemaQuery = useQuery(filterSchemaQuery());

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [definition, setDefinition] = useState<GroupFilterNode>({
    $type: "group",
    combinator: "and",
    filters: [],
  });
  const [evaluationResult, setEvaluationResult] = useState<SegmentEvaluationResult | null>(null);
  const [evaluating, setEvaluating] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [initialized, setInitialized] = useState(false);

  const createMut = useCreateSegmentMutation();
  const updateMut = useUpdateSegmentMutation(segmentId);
  const filterTypes = useMemo(() => schemaQuery.data ?? [], [schemaQuery.data]);

  // Wrapper that clears stale evaluation when the definition changes
  const updateDefinition = useCallback((updater: (d: GroupFilterNode) => GroupFilterNode) => {
    setDefinition(updater);
    setEvaluationResult(null);
  }, []);

  // Mark as initialized once schema loads (for new segments)
  useEffect(() => {
    if (isNew && filterTypes.length > 0 && !initialized) {
      setInitialized(true);
    }
  }, [isNew, filterTypes, initialized]);

  useEffect(() => {
    if (query.data) {
      setName(query.data.name);
      setDescription(query.data.description || "");
      setDefinition(query.data.definition as unknown as GroupFilterNode);
      setInitialized(true);
    }
  }, [query.data]);

  const handleEvaluate = useCallback(async () => {
    setEvaluating(true);
    try {
      const result = await evaluateSegmentDefinition(definition);
      setEvaluationResult(result);
    } catch (err) {
      toast.error("Failed to evaluate segment");
    } finally {
      setEvaluating(false);
    }
  }, [definition]);

  function validate() {
    const newErrors: Record<string, string> = {};
    if (!name.trim()) newErrors.name = "Name is required";
    if (!definition.filters?.length) newErrors.filters = "At least one filter is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  async function handleSave() {
    if (!validate()) return;
    const body = { name, description: description || undefined, definition };

    if (isNew) {
      createMut.mutate(body, {
        onSuccess: data => {
          toast.success("Segment created");
          navigate({
            to: "/campaigns/segments/$segmentId",
            params: { segmentId: data.segment_id },
          });
        },
      });
    } else {
      updateMut.mutate(body, {
        onSuccess: () => toast.success("Segment saved"),
      });
    }
  }

  const isPending = createMut.isPending || updateMut.isPending;

  return (
    <PageWrapper
      title={isNew ? "New Segment" : "Edit Segment"}
      rightContent={
        <Flex css={{ gap: 8 }}>
          <Button variant="plain" color="neutral" onClick={() => navigate({ to: "/campaigns/segments" })}>
            Cancel
          </Button>
          <Button variant="tonal" color="primary" onClick={handleSave} loading={isPending} loadingText="Saving...">
            {isNew ? "Create" : "Save"}
          </Button>
        </Flex>
      }
    >
      <Flex css={{ gap: 16, items: "start" }}>
        <Box css={{ flex: "2 0 0px", spaceY: 16, background: "white", p: 16, rounded: 12 }}>
          <Field>
            <Label>Name</Label>
            <Input value={name} onValueChange={setName} placeholder="e.g. Active NZ members" />
            {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
          </Field>
          <Field>
            <Label>Description</Label>
            <Input value={description} onValueChange={setDescription} placeholder="Optional description" />
          </Field>

          {/* Dark conditions panel */}
          <div
            style={{
              marginTop: 16,
              background: "#2d2d2d",
              borderRadius: 8,
              padding: 12,
              border: "1px solid rgba(255,255,255,0.08)",
            }}
          >
            <div style={{ fontWeight: 600, color: "#e0e0e0", marginBottom: 8, fontSize: 14 }}>Conditions</div>
            {errors.filters && <ErrorMessage>{errors.filters}</ErrorMessage>}

            <FilterListRenderer
              filters={definition.filters ?? []}
              combinator={definition.combinator ?? "and"}
              filterTypes={filterTypes}
              filterMatches={evaluationResult?.filter_matches}
              onCombinatorChange={v => updateDefinition(d => ({ ...d, combinator: v }))}
              onFilterChange={(i, updated) => {
                updateDefinition(d => ({
                  ...d,
                  filters: (d.filters ?? []).map((f, idx) => (idx === i ? updated : f)),
                }));
              }}
              onFilterRemove={i => {
                updateDefinition(d => ({
                  ...d,
                  filters: (d.filters ?? []).filter((_, idx) => idx !== i),
                }));
              }}
              depth={0}
            />

            <AddConditionControls
              filterTypes={filterTypes}
              onAddFilter={node => updateDefinition(d => ({ ...d, filters: [...(d.filters ?? []), node] }))}
              onAddGroup={() => updateDefinition(d => ({ ...d, filters: [...(d.filters ?? []), createEmptyGroup()] }))}
            />
          </div>

          {/* Evaluate button stays in light theme */}
          <Flex css={{ mt: 16 }}>
            <Button variant="outlined" color="primary" onClick={handleEvaluate} loading={evaluating}>
              Evaluate Segment
            </Button>
          </Flex>
        </Box>

        <Box css={{ flex: "1 0 0px" }}>
          <Box css={{ background: "white", p: 16, rounded: 12 }}>
            <Text css={{ fontWeight: 600, mb: 12 }}>Members</Text>
            {evaluationResult ? (
              <>
                <Text css={{ fontScale: "sm", mb: 12 }}>
                  {evaluationResult.count} member{evaluationResult.count !== 1 && "s"} matched
                </Text>
                <Box css={{ maxHeight: 400, overflow: "auto" }}>
                  {evaluationResult.members.map(m => (
                    <Flex
                      key={m.member_id}
                      css={{
                        py: 6,
                        px: 8,
                        borderBottom: `1px solid ${colors.outline}`,
                        gap: 8,
                        items: "center",
                      }}
                    >
                      <Box>
                        <StyledLink to="/admin/members/$memberId" params={{ memberId: m.member_id }} target="_blank">
                          <Text css={{ fontScale: "sm", fontWeight: 500 }}>
                            {m.given_name} {m.family_name}
                          </Text>
                        </StyledLink>
                        <Text css={{ fontScale: "xs", color: colors.text2 }}>{m.email}</Text>
                      </Box>
                    </Flex>
                  ))}
                </Box>
              </>
            ) : (
              <Text css={{ fontScale: "sm", color: colors.text2 }}>
                Click "Evaluate Segment" to see matching members.
              </Text>
            )}
          </Box>
        </Box>
      </Flex>
    </PageWrapper>
  );
}

// -- Add condition dropdown + Add group button --

function AddConditionControls({
  filterTypes,
  onAddFilter,
  onAddGroup,
  depth = 0,
}: {
  filterTypes: FilterTypeSchema[];
  onAddFilter: (node: FilterNode) => void;
  onAddGroup: () => void;
  depth?: number;
}) {
  return (
    <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
      <select
        style={addSelectStyle}
        value=""
        onChange={e => {
          const v = e.target.value;
          if (!v) return;
          const ft = filterTypes.find(t => t.type === v);
          if (ft) onAddFilter(createFilterFromSchema(ft));
        }}
      >
        <option value="" disabled>
          + Add condition
        </option>
        {filterTypes.map(ft => (
          <option key={ft.type} value={ft.type}>
            {ft.label}
          </option>
        ))}
      </select>
      {depth < 2 && (
        <button style={addButtonStyle} onClick={onAddGroup}>
          + Add group
        </button>
      )}
    </div>
  );
}

// -- Filter list with left-gutter combinator badges --

function FilterListRenderer({
  filters,
  combinator,
  filterTypes,
  filterMatches,
  onCombinatorChange,
  onFilterChange,
  onFilterRemove,
  depth,
}: {
  filters: FilterNode[];
  combinator: "and" | "or";
  filterTypes: FilterTypeSchema[];
  filterMatches?: FilterMatchInfo[];
  onCombinatorChange: (v: "and" | "or") => void;
  onFilterChange: (index: number, node: FilterNode) => void;
  onFilterRemove: (index: number) => void;
  depth: number;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      {filters.map((filter, i) => (
        <Fragment key={i}>
          {/* Combinator badge — own row, left-aligned in the gutter */}
          {i > 0 && (
            <div style={{ width: 52, display: "flex", justifyContent: "center", padding: "1px 0" }}>
              <select
                style={combinatorSelect}
                value={combinator}
                onChange={e => onCombinatorChange(e.target.value as "and" | "or")}
              >
                <option value="and">and</option>
                <option value="or">or</option>
              </select>
            </div>
          )}
          {/* Filter row — gutter spacer + content */}
          <div style={{ display: "flex", gap: 6 }}>
            <div style={{ width: 52, flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <FilterNodeEditor
                node={filter}
                filterTypes={filterTypes}
                matchInfo={filterMatches?.[i]}
                depth={depth}
                onChange={updated => onFilterChange(i, updated)}
                onRemove={() => onFilterRemove(i)}
              />
            </div>
          </div>
        </Fragment>
      ))}
    </div>
  );
}

// -- Filter node editor --

function FilterNodeEditor({
  node,
  filterTypes,
  matchInfo,
  onChange,
  onRemove,
  depth = 0,
}: {
  node: FilterNode;
  filterTypes: FilterTypeSchema[];
  matchInfo?: FilterMatchInfo;
  onChange: (node: FilterNode) => void;
  onRemove: () => void;
  depth?: number;
}) {
  if (isGroupFilterNode(node)) {
    return (
      <div
        style={{
          background: "#333",
          borderRadius: 6,
          border: "1px solid rgba(255,255,255,0.08)",
          borderLeft: "3px solid rgba(255,255,255,0.2)",
          padding: 8,
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ color: "#e0e0e0", fontSize: 12, fontWeight: 600 }}>Group</span>
            <MatchBadge info={matchInfo} />
          </div>
          <button style={removeButton} onClick={onRemove}>
            ✕
          </button>
        </div>

        <FilterListRenderer
          filters={node.filters || []}
          combinator={node.combinator || "and"}
          filterTypes={filterTypes}
          filterMatches={matchInfo?.children ?? undefined}
          onCombinatorChange={v => onChange({ ...node, combinator: v } as FilterNode)}
          onFilterChange={(i, updated) => {
            const newFilters = [...(node.filters || [])];
            newFilters[i] = updated;
            onChange({ ...node, filters: newFilters } as FilterNode);
          }}
          onFilterRemove={i => {
            onChange({
              ...node,
              filters: (node.filters || []).filter((_, idx) => idx !== i),
            } as FilterNode);
          }}
          depth={depth + 1}
        />

        <AddConditionControls
          filterTypes={filterTypes}
          depth={depth + 1}
          onAddFilter={newFilter => onChange({ ...node, filters: [...(node.filters || []), newFilter] } as FilterNode)}
          onAddGroup={() => onChange({ ...node, filters: [...(node.filters || []), createEmptyGroup()] } as FilterNode)}
        />
      </div>
    );
  }

  // Leaf filter row
  const currentSchema = filterTypes.find(ft => ft.type === node.$type);

  return (
    <div
      style={{
        display: "flex",
        background: "#3a3a3a",
        borderRadius: 4,
        padding: "4px 8px",
        gap: 6,
        alignItems: "center",
      }}
    >
      {/* Type label */}
      <span style={{ color: "#ccc", fontSize: 13, fontWeight: 500, minWidth: 100 }}>
        {currentSchema?.label ?? node.$type}
      </span>

      {/* Dynamic config fields */}
      {currentSchema && (
        <DynamicFilterFields
          fields={currentSchema.fields}
          values={node as Record<string, unknown>}
          onChange={(key, value) => onChange({ ...node, [key]: value })}
        />
      )}

      {/* Match count + Remove button */}
      <MatchBadge info={matchInfo} />
      <button style={{ ...removeButton, marginLeft: "auto" }} onClick={onRemove}>
        ✕
      </button>
    </div>
  );
}

// -- Generic dynamic filter fields --

function DynamicFilterFields({
  fields,
  values,
  onChange,
}: {
  fields: FilterFieldSchema[];
  values: Record<string, unknown>;
  onChange: (key: string, value: unknown) => void;
}) {
  return (
    <>
      {fields.map((field, i) => (
        <DynamicField
          key={field.key ?? `__prose_${i}`}
          field={field}
          value={field.key ? values[field.key] : undefined}
          onChange={val => {
            if (field.key) onChange(field.key, val);
          }}
        />
      ))}
    </>
  );
}

function FieldLabel({ label }: { label?: string | null }) {
  if (!label) return null;
  return <span style={{ color: "rgba(255,255,255,0.5)", fontSize: 13 }}>{label}</span>;
}

function DynamicField({
  field,
  value,
  onChange,
}: {
  field: FilterFieldSchema;
  value: unknown;
  onChange: (val: unknown) => void;
}) {
  // Prose: non-interactive inline text rendered between fields.
  if (field.$type === "prose") {
    const proseField = field as ProseFieldSchema;
    return <span style={{ color: "rgba(255,255,255,0.7)", fontSize: 13 }}>{proseField.content}</span>;
  }

  // Boolean checkbox
  if (field.$type === "bool") {
    const boolField = field as BoolFieldSchema;
    return (
      <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
        <input
          type="checkbox"
          checked={(value as boolean) ?? boolField.default_value ?? false}
          onChange={e => onChange(e.target.checked)}
          style={{ cursor: "pointer" }}
        />
        {field.label && <span style={{ color: "rgba(255,255,255,0.7)", fontSize: 13 }}>{field.label}</span>}
      </label>
    );
  }

  // Number input
  if (field.$type === "number") {
    const numberField = field as NumberFieldSchema;
    return (
      <>
        <FieldLabel label={field.label} />
        <input
          type="number"
          style={darkInput}
          value={(value as number) ?? numberField.default_value ?? ""}
          onChange={e => {
            const v = e.target.value;
            onChange(v === "" ? null : Number(v));
          }}
          placeholder={field.placeholder ?? ""}
        />
      </>
    );
  }

  // Text input
  if (field.$type === "text") {
    const textField = field as TextFieldSchema;
    return (
      <>
        <FieldLabel label={field.label} />
        <input
          type="text"
          style={darkInput}
          value={(value as string) ?? textField.default_value ?? ""}
          onChange={e => onChange(e.target.value)}
          placeholder={field.placeholder ?? ""}
        />
      </>
    );
  }

  // Textarea for pasting lists (e.g. member IDs)
  if (field.$type === "textarea") {
    const textAreaField = field as TextAreaFieldSchema;
    return (
      <>
        <FieldLabel label={field.label} />
        <TextAreaPasteField field={textAreaField} value={value} onChange={onChange} />
      </>
    );
  }

  // Select with inline options
  if (field.$type === "select") {
    const selectField = field as SelectFieldSchema;
    if (selectField.options) {
      return (
        <>
          {field.label && <span style={{ color: "rgba(255,255,255,0.5)", fontSize: 13 }}>{field.label}</span>}
          <select
            style={{ ...darkSelect, width: "auto", minWidth: 110 }}
            value={(value as string) ?? selectField.default_value ?? ""}
            onChange={e => onChange(e.target.value)}
          >
            {selectField.options.map(opt => (
              <option key={opt.value ?? ""} value={opt.value ?? ""}>
                {opt.label}
              </option>
            ))}
          </select>
        </>
      );
    }
    // Select with remote options
    if (selectField.options_endpoint) {
      return (
        <>
          {field.label && <span style={{ color: "rgba(255,255,255,0.5)", fontSize: 13 }}>{field.label}</span>}
          <RemoteSelectField field={field} value={value} onChange={onChange} />
        </>
      );
    }
  }

  // Multi-select with remote options
  if (field.$type === "multi_select") {
    const multiSelectField = field as MultiSelectFieldSchema;
    if (multiSelectField.options_endpoint) {
      return (
        <>
          {field.label && <span style={{ color: "rgba(255,255,255,0.5)", fontSize: 13 }}>{field.label}</span>}
          <RemoteSelectField field={field} value={value} onChange={onChange} />
        </>
      );
    }
  }

  // Fallback: plain select with no options
  return (
    <>
      {field.label && <span style={{ color: "rgba(255,255,255,0.5)", fontSize: 13 }}>{field.label}</span>}
      <select
        style={{ ...darkSelect, flex: 1, minWidth: 130 }}
        value={(value as string) ?? ""}
        onChange={e => onChange(e.target.value)}
      >
        <option value="">Select...</option>
      </select>
    </>
  );
}

// -- Textarea paste field --

function TextAreaPasteField({
  field,
  value,
  onChange,
}: {
  field: TextAreaFieldSchema;
  value: unknown;
  onChange: (val: unknown) => void;
}) {
  const ids = (Array.isArray(value) ? value : []) as string[];
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");

  function openDialog() {
    setDraft(ids.join("\n"));
    setOpen(true);
  }

  function handleConfirm() {
    const parsed = draft
      .split("\n")
      .map(s => s.trim())
      .filter(Boolean);
    onChange(parsed);
    setOpen(false);
  }

  const parsedCount = draft
    .split("\n")
    .map(s => s.trim())
    .filter(Boolean).length;

  return (
    <>
      <button
        style={{
          ...darkSelect,
          cursor: "pointer",
          minWidth: 140,
          textAlign: "left",
        }}
        onClick={openDialog}
        type="button"
      >
        {ids.length > 0 ? `${ids.length} ${field.label ?? "items"}` : (field.placeholder ?? field.label ?? "Paste…")}
      </button>

      {open &&
        createPortal(
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.6)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 9999,
            }}
            onClick={e => {
              if (e.target === e.currentTarget) setOpen(false);
            }}
          >
            <div
              style={{
                background: "#2d2d2d",
                border: "1px solid rgba(255,255,255,0.12)",
                borderRadius: 8,
                padding: 20,
                width: 480,
                display: "flex",
                flexDirection: "column",
                gap: 12,
              }}
            >
              <div style={{ color: "#e0e0e0", fontWeight: 600, fontSize: 14 }}>{field.label}</div>
              <div style={{ color: "rgba(255,255,255,0.5)", fontSize: 12 }}>
                One per line.
                {parsedCount > 0 && (
                  <>
                    {" "}
                    <strong style={{ color: "#aaa" }}>{parsedCount} parsed</strong>
                  </>
                )}
              </div>
              <textarea
                autoFocus
                style={{
                  background: "#444",
                  border: "1px solid rgba(255,255,255,0.15)",
                  borderRadius: 4,
                  color: "#e0e0e0",
                  fontSize: 12,
                  fontFamily: "monospace",
                  padding: "8px",
                  resize: "vertical",
                  height: 240,
                  outline: "none",
                }}
                value={draft}
                onChange={e => setDraft(e.target.value)}
                placeholder={field.placeholder ?? ""}
              />
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button style={{ ...darkSelect, cursor: "pointer" }} onClick={() => setOpen(false)} type="button">
                  Cancel
                </button>
                <button
                  style={{
                    ...darkSelect,
                    cursor: "pointer",
                    background: "#5b8dd9",
                    border: "1px solid #5b8dd9",
                    color: "#fff",
                    fontWeight: 600,
                  }}
                  onClick={handleConfirm}
                  type="button"
                >
                  Apply ({parsedCount})
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}

// -- Remote select field (fetches options lazily) --

function RemoteSelectField({
  field,
  value,
  onChange,
}: {
  field: FilterFieldSchema;
  value: unknown;
  onChange: (val: unknown) => void;
}) {
  const selectOrMulti = field as SelectFieldSchema | MultiSelectFieldSchema;

  // Extract the suffix after /segments/options/ for the endpoint key
  const endpointKey = selectOrMulti.options_endpoint?.replace(/^\/segments\/options\//, "") ?? "";
  const { data: options, isLoading } = useQuery(filterOptionsEndpointQuery(endpointKey));

  const valueField = selectOrMulti.value_field ?? "value";
  const labelField = selectOrMulti.label_field ?? "label";

  if (field.$type === "multi_select") {
    // For multi_select, value is string[] and we render a single select that adds to the array
    const selected = (Array.isArray(value) ? value : []) as string[];
    const currentValue = selected[0] ?? "";

    return (
      <select
        style={{ ...darkSelect, flex: 1, minWidth: 160 }}
        value={currentValue}
        onChange={e => {
          const v = e.target.value;
          onChange(v ? [v] : []);
        }}
      >
        <option value="">{isLoading ? "Loading..." : "Select..."}</option>
        {options?.map(opt => (
          <option key={String(opt[valueField])} value={String(opt[valueField])}>
            {String(opt[labelField])}
          </option>
        ))}
      </select>
    );
  }

  // Single select
  return (
    <select
      style={{ ...darkSelect, flex: 1, minWidth: 130 }}
      value={(value as string) ?? ""}
      onChange={e => onChange(e.target.value)}
    >
      <option value="">{isLoading ? "Loading..." : "Select..."}</option>
      {options?.map(opt => (
        <option key={String(opt[valueField])} value={String(opt[valueField])}>
          {String(opt[labelField])}
        </option>
      ))}
    </select>
  );
}
